import React, { useEffect, useState, useMemo } from "react";
import axios from "../Axios/Axios";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import { Modal } from "react-bootstrap";
import { FaSpinner } from "react-icons/fa"; // Loader icon

// Custom styles
const styles = {
  sectionTitle: {
    borderLeft: "4px solid #6fb104",
    paddingLeft: "10px",
    fontWeight: "bold",
    marginBottom: "0.5rem",
    color: "#343a40",
  },
  badge: {
    backgroundColor: "#f1f1f1",
    color: "#333",
    padding: "3px 8px",
    borderRadius: "10px",
    margin: "2px",
    fontSize: "0.85rem",
  },
};

const History = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [customerFilter, setCustomerFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalImage, setModalImage] = useState(null);
  const [loading, setLoading] = useState(false); // loader state

  const recordsPerPage = 5;

  const fetchData = async () => {
    const token = localStorage.getItem("mughalsLoggedIn");
    try {
      setLoading(true); // start loader
      const res = await axios.get("/api/assignmaterial/all", {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      setData(res.data);
      setFilteredData(res.data);
    } catch (err) {
      console.error("Failed to fetch data:", err);
      Swal.fire("Error", "Could not load records", "error");
    } finally {
      setLoading(false); // stop loader
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const display = (val) =>
    val !== undefined && val !== null && val !== "" ? val : "N/A";

  const formatDate = (iso) => {
    if (!iso) return "N/A";
    const dateObj = new Date(iso);

    const date = dateObj.toLocaleDateString();
    const time = dateObj.toLocaleTimeString();

    return `${date} | | ${time}`;
  };

  const handleDelete = async (id) => {
    const token = localStorage.getItem("mughalsLoggedIn");
    const { isConfirmed } = await Swal.fire({
      title: "Are you sure?",
      text: "This record will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      confirmButtonColor: "#6fb104",
      cancelButtonColor: "#6fb104",
    });

    if (!isConfirmed) return;

    try {
      setLoading(true); // loader start on delete
      await axios.delete(`/api/assignmaterial/delete/${id}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      await fetchData();
      Swal.fire("Deleted!", "Record has been deleted.", "success");
    } catch (err) {
      Swal.fire("Error!", "Failed to delete record.", "error");
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let temp = [...data];

    if (customerFilter.trim()) {
      temp = temp.filter((item) =>
        item.customerId?.name
          ?.toLowerCase()
          .includes(customerFilter.trim().toLowerCase())
      );
    }

    if (fromDate) {
      temp = temp.filter(
        (item) => new Date(item.assignedAt) >= new Date(fromDate)
      );
    }

    if (toDate) {
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);
      temp = temp.filter((item) => new Date(item.assignedAt) <= to);
    }

    setFilteredData(temp);
    setCurrentPage(1);
  };

  const currentRecords = useMemo(() => {
    const last = currentPage * recordsPerPage;
    const first = last - recordsPerPage;
    return filteredData.slice(first, last);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / recordsPerPage);

  // Loader component
  const LoadingIcon = ({ size = 40 }) => (
    <FaSpinner className="spin" style={{ fontSize: size, color: "#6fb104" }} />
  );

  return (
    <div className=" my-4">
      <h4 className="mb-4 fw-semibold text-start  text-dark">
        Material Assignment Records
      </h4>

      {/* Loader */}
      {loading && (
        <div className="d-flex justify-content-center my-5">
          <LoadingIcon size={50} />
        </div>
      )}

      {/* Filters */}
      {!loading && (
        <div className="card p-4 shadow-sm mb-4 border-0 rounded-4">
          <h5 className="mb-3 fw-semibold text-lemon">Filter Records</h5>
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label text-dark">Customer Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="Filter by customer"
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label text-dark">From Date</label>
              <input
                type="date"
                className="form-control"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label text-dark">To Date</label>
              <input
                type="date"
                className="form-control"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <div className="col-md-3 d-flex gap-2">
              <button
                className="btn bg-lemon text-white w-100"
                onClick={applyFilters}
              >
                Apply Filters
              </button>
              <button
                className="btn bg-lemon text-white"
                onClick={() => {
                  setCustomerFilter("");
                  setFromDate("");
                  setToDate("");
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Card List */}
      {!loading && (
        <div className="d-flex flex-column gap-3">
          {currentRecords.length > 0 ? (
            currentRecords.map((item, idx) => {
              const hasAssignedMaterials =
                Array.isArray(item.assignedMaterials) &&
                item.assignedMaterials.length > 0;

              const authorityLetterImage = hasAssignedMaterials
                ? item.assignedMaterials.find((m) => m.authorityLetter?.image)
                    ?.authorityLetter.image
                : null;

              return (
                <div
                  key={item._id}
                  className="card shadow-sm card-hover border-0 p-3 rounded-4"
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0 text-lemon">
                      #{idx + 1 + (currentPage - 1) * recordsPerPage} - Material
                      Assignment
                    </h6>
                    <button
                      className="btn btn-sm bg-lemon text-white"
                      onClick={() => handleDelete(item._id)}
                    >
                      Delete
                    </button>
                  </div>

                  {/* Customer Info */}
                  <div className="mb-2">
                    <div className="d-flex   justify-content-between  align-items-center">
                      <div>
                        <strong className="d-block text-lemon ">
                          Customer Details
                        </strong>
                        Name: {display(item.customerId?.name)} <br></br>
                        NIC: {display(item.customerNIC)}
                        <br></br>
                        License: {display(item.licenseNumber)}
                      </div>
                      <div>
                        {item.customerImage ? (
                          <img
                            src={`${axios.defaults.baseURL}/${item.customerImage}`}
                            alt="Customer"
                            className="rounded"
                            style={{
                              width: "100px",
                              height: "100px",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              setModalImage(
                                `${axios.defaults.baseURL}/${item.customerImage}`
                              )
                            }
                          />
                        ) : (
                          <span className="text-muted">No Image</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Assignment Date */}
                  <div className="mb-2">
                    <strong className="d-block text-lemon mb-1">
                      Time & Date
                    </strong>
                    {formatDate(item.assignedAt)}
                  </div>

                  {/* Slip & Batch Info */}
                  <div className="mb-2">
                    <strong className="d-block text-lemon mb-1">
                      Slip & Batch Info
                    </strong>
                    <div className="row">
                      <div className="col-md-2">
                        Slip No: {display(item.deliverySlipNo)}
                      </div>
                      <div className="col-md-2">
                        Reel No: {display(item.reelNo)}
                      </div>
                      <div className="col-md-2">
                        Lot No: {display(item.lotNo)}
                      </div>
                      <div className="col-md-2">
                        Shell No: {display(item.shellNo)}
                      </div>
                      <div className="col-md-2">
                        S-Fuse BatchNo: {display(item.sfuseBatchNo)}
                      </div>
                      <div className="col-md-2">
                        Dets BatchNo: {display(item.waboxBatchNo)}
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div className="col-md-3 ">
                        Detonator No: {display(item.detonatorNo)}
                      </div>
                    </div>
                  </div>

                  {/* Materials */}
                  <div className="mb-2">
                    <strong className="d-block text-lemon mb-1">Materials</strong>
                    <div className="d-flex flex-wrap">
                      {hasAssignedMaterials ? (
                        item.assignedMaterials.map((m, i) => (
                          <span
                            key={i}
                            className="text-dark me-2 mb-1"
                          >
                            {display(m.materialId?.name)} (
                            {display(m.assignedQuantity)})
                          </span>
                        ))
                      ) : (
                        <span className="text-dark">N/A</span>
                      )}
                    </div>
                  </div>

                  {/* Authority Letter */}
                  <div className="mb-1">
                    <strong className="d-block text-lemon mb-1">
                      Authority Letter
                    </strong>
                    {authorityLetterImage ? (
                      <img
                        src={`${axios.defaults.baseURL}/${authorityLetterImage}`}
                        alt="Authority Letter"
                        className="rounded"
                        style={{
                          width: "50px",
                          height: "50px",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          setModalImage(
                            `${axios.defaults.baseURL}/${authorityLetterImage}`
                          )
                        }
                      />
                    ) : (
                      <span className="text-muted">No Authority Letter</span>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-muted text-center py-3">No records found.</div>
          )}
        </div>
      )}

      {/* Pagination */}
      {!loading && (
        <div className="d-flex justify-content-center mt-4">
          <ul className="pagination bg-white ">
            <li className={`page-item ${currentPage === 1 && "disabled"}`}>
              <button
                className="page-link "
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              >
                <span className="text-lemon"> Previous </span>
              </button>
            </li>
            {[...Array(totalPages)].map((_, i) => (
              <li
                key={i}
                className={`page-item ${currentPage === i + 1 && "active"}`}
              >
                <button
                  className="page-link text-lemon"
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              </li>
            ))}
            <li
              className={`page-item ${currentPage === totalPages && "disabled"}`}
            >
              <button
                className="page-link "
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
              >
                <span className="text-lemon"> Next </span>
              </button>
            </li>
          </ul>
        </div>
      )}

      {/* Modal */}
      <Modal show={!!modalImage} onHide={() => setModalImage(null)} centered>
        <Modal.Body className="p-0">
          <img
            src={modalImage}
            alt="Preview"
            className="img-fluid w-100"
            style={{ maxHeight: "80vh" }}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default History;
